import { motion } from "framer-motion";
import { MapPin, Navigation, Clock } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useSite, openStatus } from "@/lib/site";

export const HoursLocation = () => {
  const { t, L } = useI18n();
  const { data } = useSite();
  const s = data?.settings || {};
  const hours = s.hours || [];
  const status = openStatus(hours);
  const mapQuery = encodeURIComponent(s.address ? `BlackBeard Barberia, ${s.address}` : "BlackBeard Barberia");

  return (
    <section id="horario" data-testid="hours-location-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <p className="font-mono text-[11px] tracking-[0.3em] text-gold mb-4">{t("hours.eyebrow")}</p>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-bone leading-[0.95]">
              {t("hours.title")}
            </h2>
          </div>
          <div
            data-testid="open-status-badge"
            className={`inline-flex items-center gap-2.5 self-start md:self-auto font-mono text-xs tracking-[0.18em] uppercase px-4 py-2.5 rounded-full border ${
              status.open
                ? "border-emerald-500/40 text-emerald-400"
                : "border-gold/30 text-gold"
            }`}
          >
            <span
              className={`h-2 w-2 rounded-full ${status.open ? "bg-emerald-400 animate-pulse" : "bg-gold"}`}
            />
            {status.open ? t("hours.open_now") : t("hours.closed_now")}
          </div>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-6 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6 }}
            className="border border-gold/10 bg-panel p-8 sm:p-10 flex flex-col"
            data-testid="hours-panel"
          >
            <div className="flex items-center gap-3 text-gold mb-6">
              <Clock size={18} />
              <span className="font-mono text-xs tracking-[0.25em] uppercase">{t("hours.title")}</span>
            </div>
            {hours.length === 0 ? (
              <p className="text-mut text-sm" data-testid="hours-empty-state">—</p>
            ) : (
              <div className="flex flex-col">
                {hours.map((h, i) => (
                  <div
                    key={i}
                    className="flex items-baseline justify-between gap-4 py-4 border-b border-white/5 last:border-0"
                  >
                    <span className="text-bone/90">{L(h.days_es, h.days_en)}</span>
                    <span className="font-mono text-sm text-gold/90 whitespace-nowrap">
                      {h.open} — {h.close}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {s.about_es && (
              <p className="mt-auto pt-8 text-sm text-mut leading-relaxed">{L(s.about_es, s.about_en)}</p>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="border border-gold/10 bg-panel overflow-hidden flex flex-col"
            data-testid="location-panel"
          >
            <iframe
              title="Blackbeard Barbería — Google Maps"
              src={`https://www.google.com/maps?q=${mapQuery}&output=embed`}
              className="w-full h-64 lg:h-full lg:min-h-[320px]"
              style={{ border: 0, filter: "grayscale(0.9) invert(0.92) contrast(0.85)" }}
              loading="lazy"
            />
            <div className="p-6 flex flex-col gap-3">
              <div className="flex items-start gap-3 text-sm text-mut">
                <MapPin size={16} className="text-gold shrink-0 mt-0.5" />
                <span data-testid="location-address">{s.address || "—"}</span>
              </div>
              <a
                data-testid="google-maps-location-link"
                href={s.maps_url || "https://maps.app.goo.gl/BDbAViHettaMaqydA"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-gold/40 text-gold font-semibold text-sm px-5 py-3 rounded-full hover:bg-gold hover:text-ink transition-colors"
              >
                <Navigation size={15} />
                {t("hours.maps_cta")}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HoursLocation;
